import type { ReactNode } from 'react'

import { Badge, Icon } from '@speaking-coach/ui'
import type { CoooriAdmin } from './types.js'

function AccessState({
  icon,
  label,
  description,
  variant,
}: {
  icon: ReactNode
  label: string
  description: string
  variant: 'neutral' | 'danger' | 'muted'
}): JSX.Element {
  return (
    <div>
      <div className="flex items-center gap-2">
        {icon}
        <Badge variant={variant}>{label}</Badge>
      </div>
      <p className="mt-1 max-w-xs pl-6 text-caption text-text-secondary">
        {description}
      </p>
    </div>
  )
}

export function AdminAccessState({ admin }: { admin: CoooriAdmin }): JSX.Element {
  if (admin.activationState === 'active') {
    return (
      <AccessState
        icon={<Icon name="success" className="shrink-0 text-success-text" />}
        label="Active"
        description="Can sign in to Admin."
        variant="neutral"
      />
    )
  }

  const delivery = admin.lastDelivery
  if (delivery === null) {
    return (
      <AccessState
        icon={<Icon name="warning" className="shrink-0 text-danger-text" />}
        label="Invitation missing"
        description="No delivery was recorded."
        variant="danger"
      />
    )
  }

  if (delivery.status === 'failed_terminal') {
    return (
      <AccessState
        icon={<Icon name="inviteFailed" className="shrink-0 text-danger-text" />}
        label="Delivery failed"
        description={delivery.failureReason ?? 'The email could not be delivered.'}
        variant="danger"
      />
    )
  }

  if (delivery.status === 'retryable') {
    return (
      <AccessState
        icon={<Icon name="resend" className="shrink-0 text-text-secondary" />}
        label="Retry queued"
        description="Delivery will be tried again."
        variant="muted"
      />
    )
  }

  if (delivery.status === 'expired') {
    return (
      <AccessState
        icon={<Icon name="inviteExpired" className="shrink-0 text-danger-text" />}
        label="Invite expired"
        description="Send a new link to continue."
        variant="danger"
      />
    )
  }

  if (delivery.status === 'sent') {
    return (
      <AccessState
        icon={<Icon name="invitePending" className="shrink-0 text-text-primary" />}
        label="Invite sent"
        description="Waiting for activation."
        variant="muted"
      />
    )
  }

  return (
    <AccessState
      icon={<Icon name="time" className="shrink-0 text-text-secondary" />}
      label="Invite queued"
      description="Waiting for email delivery."
      variant="muted"
    />
  )
}
